import React, { useEffect, useState } from "react";
import {
  Container, Row, Col, Input, Button, Spinner,
  Accordion, AccordionItem, AccordionHeader, AccordionBody,
  Modal, ModalHeader, ModalBody, ModalFooter, Form, FormGroup, Label
} from "reactstrap";
import { getFaqs, addFaq, updateFaq } from "../../API/Backend";
import { useClient } from "../../Utilities/ClientContext";

export default function FaqAdmin() {
  const { activeClient } = useClient();
  const [faqList, setFaqList] = useState([]);
  const [filteredFaqs, setFilteredFaqs] = useState([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [loading, setLoading] = useState(true);
  const [modalOpen, setModalOpen] = useState(false);
  const [formState, setFormState] = useState({ question: "", answer: "" });
  const [editIndex, setEditIndex] = useState(null);
  const [faqDocId, setFaqDocId] = useState("");
  const [accordionOpen, setAccordionOpen] = useState(null);

  const fetchData = async () => {
    setLoading(true);
    try {
      const res = await getFaqs({ clientName: activeClient, type: "faq" });
      const entry = res.data.data.find((d) => d.type === "faq");
      if (entry) {
        setFaqList(entry.data || []);
        setFilteredFaqs(entry.data || []);
        setFaqDocId(entry._id);
      } else {
        setFaqList([]);
        setFilteredFaqs([]);
        setFaqDocId("");
      }
    } catch (err) {
      console.error("Error fetching FAQs", err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (!activeClient) return;
    fetchData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeClient]);

  // Client-side filter (live search)
  useEffect(() => {
    const term = searchTerm.trim().toLowerCase();
    if (!term) {
      setFilteredFaqs(faqList);
    } else {
      const filtered = faqList.filter(
        ({ question, answer }) =>
          question.toLowerCase().includes(term) ||
          answer.toLowerCase().includes(term)
      );
      setFilteredFaqs(filtered);
      setAccordionOpen(filtered.length ? "0" : null);
    }
  }, [searchTerm, faqList]);

  const toggleAccordion = (id) => {
    setAccordionOpen(accordionOpen === id ? null : id);
  };

  const openModal = (faq = { question: "", answer: "" }, idx = null) => {
    setFormState(faq);
    setEditIndex(idx);
    setModalOpen(true);
  };

  const closeModal = () => {
    setModalOpen(false);
    setFormState({ question: "", answer: "" });
    setEditIndex(null);
  };

  const handleSave = async () => {
    const updated = [...faqList];
    if (editIndex !== null) updated[editIndex] = formState;
    else updated.push(formState);
    try {
      if (faqDocId) {
        await updateFaq({ data: updated, type: "faq", client: activeClient }, faqDocId);
      } else {
        await addFaq({ data: updated, type: "faq", client: activeClient });
      }
      fetchData();
      closeModal();
    } catch (err) {
      console.error("Error saving FAQ", err);
    }
  };

  const handleDelete = async (idx) => {
    if (!window.confirm("Delete this FAQ?")) return;
    const updated = [...faqList];
    updated.splice(idx, 1);
    try {
      await updateFaq({ data: updated, type: "faq", client: activeClient }, faqDocId);
      fetchData();
    } catch (err) {
      console.error("Error deleting FAQ", err);
    }
  };

  return (
    <Container fluid className="py-4">
      <Row className="align-items-center mb-3">
        <Col md="6">
          <Input
            type="search"
            placeholder="Search FAQs..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
          />
        </Col>
        <Col className="text-end">
          <Button color="primary" onClick={() => openModal()}>
            Add FAQ
          </Button>
        </Col>
      </Row>

      {loading ? (
        <Spinner color="primary" />
      ) : (
        <>
          {filteredFaqs.length === 0 && (
            <div className="text-center text-muted">No FAQs found.</div>
          )}
          <Accordion open={accordionOpen} toggle={toggleAccordion}>
            {filteredFaqs.map((faq, i) => (
              <AccordionItem key={i}>
                <AccordionHeader targetId={i.toString()}>
                  {faq.question}
                </AccordionHeader>
                <AccordionBody accordionId={i.toString()}>
                  <div dangerouslySetInnerHTML={{ __html: faq.answer }} />
                  <div className="mt-3 d-flex gap-2">
                    <Button className="btn btn-outline-primary" onClick={() => openModal(faq, i)}>
                      Edit
                    </Button>
                    <Button size="sm" outline color="danger" onClick={() => handleDelete(i)}>
                      Delete
                    </Button>
                  </div>
                </AccordionBody>
              </AccordionItem>
            ))}
          </Accordion>
        </>
      )}

      <Modal isOpen={modalOpen} toggle={closeModal}>
        <ModalHeader toggle={closeModal}>
          {editIndex !== null ? "Edit FAQ" : "Add FAQ"}
        </ModalHeader>
        <ModalBody>
          <Form>
            <FormGroup>
              <Label for="question">Question</Label>
              <Input
                id="question"
                value={formState.question}
                onChange={(e) => setFormState({ ...formState, question: e.target.value })}
              />
            </FormGroup>
            <FormGroup>
              <Label for="answer">Answer</Label>
              <Input
                id="answer"
                type="textarea"
                rows="4"
                value={formState.answer}
                onChange={(e) => setFormState({ ...formState, answer: e.target.value })}
              />
            </FormGroup>
          </Form>
        </ModalBody>
        <ModalFooter>
          <Button className="btn btn-outline-primary" onClick={closeModal}>Cancel</Button>
          <Button color="primary" onClick={handleSave}>
            {editIndex !== null ? "Update" : "Add"}
          </Button>
        </ModalFooter>
      </Modal>
    </Container>
  );
}
